/**
 * A matemática de cor — sem dependência, e por um motivo.
 *
 * ## Por que não `culori`
 *
 * A biblioteca resolveria isto e mais quinze coisas. Mas este arquivo é
 * importado pelo **navegador** (o crítico de design roda a cada mexida no
 * seletor de cor, como a régua de SEO roda a cada tecla), e trinta quilobytes
 * de conversão de espaço de cor entram no bundle do painel para calcular uma
 * razão de contraste.
 *
 * O que de fato precisamos é pequeno e fechado: ler as formas de cor que o
 * `getComputedStyle` devolve, calcular luminância relativa, e comparar dois
 * tons. Isso cabe aqui, é testável, e não muda nunca — a fórmula da WCAG é de
 * 2008.
 *
 * ## A premissa que era falsa
 *
 * A versão anterior aceitava só hex, quatro nomes e `rgb()/rgba()`, e o motivo
 * declarado era este: *"a fonte é o navegador, e `getComputedStyle` normaliza
 * tudo para `rgb(r, g, b)`"*. **Não normaliza.** O Chromium atual devolve o
 * estilo computado **no espaço em que o autor escreveu**: quem escreve
 * `color(srgb 0.0726274 0.0647843 0.0672941)` recebe essa mesma string de
 * volta, e o mesmo vale para `oklch()`, `lab()` e `color(display-p3 …)` — que
 * é o que metade dos sites novos usa.
 *
 * O preço da premissa foi medido contra uma página **preta com texto creme**:
 * `parseCor` devolvia `null` para as 24 cores dela, `ehInvisivel(null)` era
 * `true`, e a redução concluía "nenhum fundo pintado — assumindo branco" e
 * "nenhuma cor de texto atinge contraste 3:1". Creme sobre branco, com dois
 * avisos culpando o site alheio por um defeito nosso.
 *
 * Por isso o parser agora lê os espaços que o navegador devolve. A conta extra
 * é matriz e função de transferência — umas quatro dúzias de linhas, não os
 * trinta quilobytes de `culori` —, e ela não muda nunca: as matrizes são do
 * CSS Color 4 e a OKLab é de 2020.
 *
 * ## Ilegível não é transparente
 *
 * `parseCor` devolve `Rgb | null`, e `null` respondia duas perguntas
 * diferentes com a mesma palavra: *"esta cor não pinta nada"* e *"não sei ler
 * esta cor"*. A primeira é um fato sobre a página; a segunda é uma confissão
 * nossa, e o silêncio entre as duas é o que produziu o aviso mentiroso lá em
 * cima. `lerCor` separa as duas — quem precisa distinguir chama ela; quem só
 * quer a cor continua chamando `parseCor`.
 *
 * O extrator original tentou o contrário — regex sobre o CSS declarado — e por
 * isso a saída dele tem `"hsl(var(--foreground)"` truncado no parêntese e
 * `rgb(255255255/1)` com o espaço removido. Ler do navegador dispensa o
 * problema em vez de resolvê-lo; ler os espaços modernos é o que faltava para
 * a leitura valer.
 */

export interface Rgb {
  r: number;
  g: number;
  b: number;
  /** 0 a 1. */
  a: number;
}

/**
 * As cores por nome que o CSS tem e o navegador pode devolver.
 *
 * A lista completa tem 148 entradas e não vale o peso: `getComputedStyle`
 * devolve `rgb()` para todas elas. Estas quatro entram porque aparecem em
 * atributo digitado à mão e em valor de fallback — e `transparent` porque ele é
 * a resposta certa para "sem fundo", que é diferente de "branco".
 */
const NOMES: Record<string, Rgb> = {
  transparent: { r: 0, g: 0, b: 0, a: 0 },
  white: { r: 255, g: 255, b: 255, a: 1 },
  black: { r: 0, g: 0, b: 0, a: 1 },
  currentcolor: { r: 0, g: 0, b: 0, a: 1 },
};

function limitar(n: number, min: number, max: number): number {
  return n < min ? min : n > max ? max : n;
}

/** Um número CSS solto — sem unidade, sem `calc()`, sem `var()`. */
const NUMERO = /^[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?$/i;

/**
 * Um componente numérico: `0.5`, `50%` ou `none`.
 *
 * `cem` é quanto vale 100% **naquela função** — 125 para o `a` de `lab()`, 0.4
 * para o `C` de `oklch()`, 1 para `color(srgb …)`. Sem esse parâmetro, uma
 * porcentagem viraria sempre 0 a 1 e `lab(50% 40% 0)` sairia quase cinza.
 *
 * Ilegível devolve `NaN` de propósito: quem chama testa `Number.isFinite` e
 * recusa a cor inteira, em vez de silenciar o componente em zero.
 */
function num(texto: string | undefined, cem = 1): number {
  if (texto === undefined) return Number.NaN;
  const t = texto.trim();
  /* `none` é o "componente ausente" do CSS Color 4, e vale zero em toda conta
     que não seja interpolação — que não é o que fazemos aqui. */
  if (t === 'none') return 0;
  if (t.endsWith('%')) {
    const corpo = t.slice(0, -1);
    return NUMERO.test(corpo) ? (Number.parseFloat(corpo) / 100) * cem : Number.NaN;
  }
  return NUMERO.test(t) ? Number.parseFloat(t) : Number.NaN;
}

/**
 * O componente em que **número solto também é porcentagem** — `hsl()` e `hwb()`.
 *
 * `hsl(210 50 40)` e `hsl(210 50% 40%)` são a mesma cor pelo CSS Color 4. Tratar
 * o primeiro como 50 em vez de 0,5 daria saturação estourada e claridade branca.
 */
function fracao(texto: string | undefined): number {
  const n = num(texto, 100);
  return Number.isFinite(n) ? n / 100 : Number.NaN;
}

/** Lê `50%` ou `0.5` como 0 a 1. Alfa ilegível vale 1 — opaco é o padrão do CSS. */
function alfa(texto: string | undefined): number {
  const n = num(texto);
  return Number.isFinite(n) ? limitar(n, 0, 1) : 1;
}

/** Lê `255` ou `100%` como 0 a 255. `NaN` quando não é número. */
function canal(texto: string | undefined): number {
  const t = texto?.trim();
  const n = t?.endsWith('%') ? num(t, 255) : num(t);
  return Number.isFinite(n) ? limitar(Math.round(n), 0, 255) : Number.NaN;
}

/** Graus, aceitando `deg`, `rad`, `grad` e `turn`. */
function angulo(texto: string | undefined): number {
  if (texto === undefined) return Number.NaN;
  const t = texto.trim();
  if (t === 'none') return 0;
  const m = /^([+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?)(deg|rad|grad|turn)?$/i.exec(t);
  if (!m) return Number.NaN;
  const n = Number.parseFloat(m[1]!);
  switch ((m[2] ?? 'deg').toLowerCase()) {
    case 'rad':
      return (n * 180) / Math.PI;
    case 'grad':
      return n * 0.9;
    case 'turn':
      return n * 360;
    default:
      return n;
  }
}

// ─── Espaços de cor ─────────────────────────────────────────────────────────
//
// Tudo converge para sRGB gama-codificado de 0 a 255, porque é o que a régua de
// contraste da WCAG mede. As matrizes são as do CSS Color 4, copiadas com todas
// as casas: arredondá-las desloca tons escuros o bastante para mudar de faixa
// de contraste.

/** sRGB linear → sRGB com gama (a transferência é a mesma do display-p3). */
function comGama(c: number): number {
  const s = Math.abs(c);
  const v = s <= 0.0031308 ? 12.92 * s : 1.055 * s ** (1 / 2.4) - 0.055;
  return c < 0 ? -v : v;
}

/** sRGB com gama → sRGB linear. */
function semGama(c: number): number {
  const s = Math.abs(c);
  const v = s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  return c < 0 ? -v : v;
}

type Vetor3 = [number, number, number];

function matriz(m: readonly number[], v: Vetor3): Vetor3 {
  return [
    m[0]! * v[0] + m[1]! * v[1] + m[2]! * v[2],
    m[3]! * v[0] + m[4]! * v[1] + m[5]! * v[2],
    m[6]! * v[0] + m[7]! * v[1] + m[8]! * v[2],
  ];
}

const XYZ_D65_PARA_SRGB_LINEAR = [
  3.2409699419045226, -1.537383177570094, -0.4986107602930034,
  -0.9692436362808796, 1.8759675015077202, 0.04155505740717559,
  0.05563007969699366, -0.20397695888897652, 1.0569715142428786,
] as const;

const P3_LINEAR_PARA_XYZ_D65 = [
  0.4865709486482162, 0.26566769316909306, 0.1982172852343625,
  0.2289745640697488, 0.6917385218365064, 0.079286914093745,
  0.0, 0.045113381858902575, 1.043944368900976,
] as const;

/** Bradford. `lab()` e `lch()` são D50 por definição; o resto do mundo é D65. */
const XYZ_D50_PARA_D65 = [
  0.9554734527042182, -0.023098536874261423, 0.0632593086610217,
  -0.028369706963208136, 1.0099954580058226, 0.021041398966943008,
  0.012314001688319899, -0.020507696433477912, 1.3303659366080753,
] as const;

/** Fecha uma cor a partir de sRGB de 0 a 1 — fora de gama é grampeado. */
function de01(r: number, g: number, b: number, a: number): Rgb | null {
  if (!Number.isFinite(r) || !Number.isFinite(g) || !Number.isFinite(b)) return null;
  return {
    r: limitar(Math.round(r * 255), 0, 255),
    g: limitar(Math.round(g * 255), 0, 255),
    b: limitar(Math.round(b * 255), 0, 255),
    a,
  };
}

function deSrgbLinear(v: Vetor3, a: number): Rgb | null {
  return de01(comGama(v[0]), comGama(v[1]), comGama(v[2]), a);
}

function deXyzD65(v: Vetor3, a: number): Rgb | null {
  return deSrgbLinear(matriz(XYZ_D65_PARA_SRGB_LINEAR, v), a);
}

/** O sextante da roda de matiz — compartilhado por `hsl()` e `hwb()`. */
function sextante(h: number, c: number, x: number): Vetor3 {
  if (h < 60) return [c, x, 0];
  if (h < 120) return [x, c, 0];
  if (h < 180) return [0, c, x];
  if (h < 240) return [0, x, c];
  if (h < 300) return [x, 0, c];
  return [c, 0, x];
}

function deHsl(h: number, s: number, l: number, a: number): Rgb | null {
  if (!Number.isFinite(h) || !Number.isFinite(s) || !Number.isFinite(l)) return null;
  const sn = limitar(s, 0, 1);
  const ln = limitar(l, 0, 1);
  const hh = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * ln - 1)) * sn;
  const x = c * (1 - Math.abs(((hh / 60) % 2) - 1));
  const m = ln - c / 2;
  const v = sextante(hh, c, x);
  return de01(v[0] + m, v[1] + m, v[2] + m, a);
}

function deHwb(h: number, w: number, p: number, a: number): Rgb | null {
  if (!Number.isFinite(h) || !Number.isFinite(w) || !Number.isFinite(p)) return null;
  const wn = limitar(w, 0, 1);
  const pn = limitar(p, 0, 1);
  /* Branco e preto que somam 1 ou mais colapsam para cinza — a regra do CSS,
     e sem ela `hwb(0 60% 60%)` sairia com matiz que ninguém vê. */
  if (wn + pn >= 1) {
    const cinza = wn / (wn + pn);
    return de01(cinza, cinza, cinza, a);
  }
  const hh = ((h % 360) + 360) % 360;
  const x = 1 - Math.abs(((hh / 60) % 2) - 1);
  const v = sextante(hh, 1, x);
  const escala = (c: number) => c * (1 - wn - pn) + wn;
  return de01(escala(v[0]), escala(v[1]), escala(v[2]), a);
}

/** OKLab → sRGB. Coeficientes de Björn Ottosson, os mesmos do CSS Color 4. */
function deOklab(l: number, aa: number, bb: number, a: number): Rgb | null {
  if (!Number.isFinite(l) || !Number.isFinite(aa) || !Number.isFinite(bb)) return null;
  const ll = (l + 0.3963377774 * aa + 0.2158037573 * bb) ** 3;
  const mm = (l - 0.1055613458 * aa - 0.0638541728 * bb) ** 3;
  const ss = (l - 0.0894841775 * aa - 1.291485548 * bb) ** 3;
  return deSrgbLinear(
    [
      4.0767416621 * ll - 3.3077115913 * mm + 0.2309699292 * ss,
      -1.2684380046 * ll + 2.6097574011 * mm - 0.3413193965 * ss,
      -0.0041960863 * ll - 0.7034186147 * mm + 1.707614701 * ss,
    ],
    a,
  );
}

const EPSILON = 216 / 24389;
const KAPPA = 24389 / 27;
/** O branco D50, que é o de `lab()`. */
const D50: Vetor3 = [0.3457 / 0.3585, 1, (1 - 0.3457 - 0.3585) / 0.3585];

function deLab(l: number, aa: number, bb: number, a: number): Rgb | null {
  if (!Number.isFinite(l) || !Number.isFinite(aa) || !Number.isFinite(bb)) return null;
  const fy = (l + 16) / 116;
  const fx = fy + aa / 500;
  const fz = fy - bb / 200;
  const x = fx ** 3 > EPSILON ? fx ** 3 : (116 * fx - 16) / KAPPA;
  const y = l > KAPPA * EPSILON ? fy ** 3 : l / KAPPA;
  const z = fz ** 3 > EPSILON ? fz ** 3 : (116 * fz - 16) / KAPPA;
  const d50: Vetor3 = [x * D50[0], y * D50[1], z * D50[2]];
  return deXyzD65(matriz(XYZ_D50_PARA_D65, d50), a);
}

/** A parte polar de `lch()` e `oklch()`: croma e matiz viram os dois eixos. */
function polar(c: number, h: number): [number, number] {
  const rad = (h * Math.PI) / 180;
  return [c * Math.cos(rad), c * Math.sin(rad)];
}

export type MotivoDaLeitura =
  /** A cor foi entendida. */
  | 'lida'
  /** Não havia valor — string vazia, `null` ou `none`. Não é cor nenhuma. */
  | 'vazia'
  /** Havia valor e **eu** não soube ler. Isto é um defeito nosso, não da página. */
  | 'ilegivel';

export interface LeituraDeCor {
  cor: Rgb | null;
  motivo: MotivoDaLeitura;
}

const VAZIA: LeituraDeCor = { cor: null, motivo: 'vazia' };
const ILEGIVEL: LeituraDeCor = { cor: null, motivo: 'ilegivel' };

/**
 * A leitura de uma cor, **com o motivo** quando não deu.
 *
 * Existe para que "não consegui ler 24 cores desta página" possa ser dito. Ver
 * o cabeçalho do arquivo: enquanto ilegível e transparente eram o mesmo `null`,
 * a redução acusava a página de não ter contraste por não saber ler `oklch()`.
 */
export function lerCor(valor: string | null | undefined): LeituraDeCor {
  if (valor === null || valor === undefined) return VAZIA;
  const v = valor.trim().toLowerCase();
  if (v === '' || v === 'none') return VAZIA;

  const nome = NOMES[v];
  if (nome) return { cor: nome, motivo: 'lida' };

  if (v.startsWith('#')) {
    const h = v.slice(1);
    if (!/^[0-9a-f]+$/.test(h)) return ILEGIVEL;
    const expandir = (c: string) => Number.parseInt(c + c, 16);
    if (h.length === 3 || h.length === 4) {
      return {
        cor: {
          r: expandir(h[0]!),
          g: expandir(h[1]!),
          b: expandir(h[2]!),
          a: h.length === 4 ? expandir(h[3]!) / 255 : 1,
        },
        motivo: 'lida',
      };
    }
    if (h.length === 6 || h.length === 8) {
      const par = (i: number) => Number.parseInt(h.slice(i, i + 2), 16);
      return {
        cor: { r: par(0), g: par(2), b: par(4), a: h.length === 8 ? par(6) / 255 : 1 },
        motivo: 'lida',
      };
    }
    return ILEGIVEL;
  }

  /*
   * Toda função de cor cabe na mesma quebra.
   *
   * A sintaxe antiga separa por vírgula (`rgb(255, 0, 0)`), a moderna por
   * espaço com o alfa depois de uma barra (`oklch(0.5 0.1 200 / 50%)`).
   * Normalizar a barra para vírgula e depois quebrar por vírgula-ou-espaço
   * atende as duas sem dois caminhos — e sem a armadilha do extrator antigo,
   * que removia todo espaço em branco e com isso colava os três canais num
   * número só (`rgb(255255255/1)`, que continua ilegível e deve continuar).
   */
  const fn = /^([a-z][a-z0-9-]*)\(([^()]*)\)$/.exec(v);
  if (!fn) return ILEGIVEL;

  const funcao = fn[1]!;
  const p = fn[2]!.replace(/\//g, ',').split(/[,\s]+/).filter(Boolean);
  const fechar = (cor: Rgb | null): LeituraDeCor => (cor ? { cor, motivo: 'lida' } : ILEGIVEL);

  switch (funcao) {
    case 'rgb':
    case 'rgba': {
      if (p.length < 3) return ILEGIVEL;
      const cor = { r: canal(p[0]), g: canal(p[1]), b: canal(p[2]), a: alfa(p[3]) };
      return Number.isFinite(cor.r) && Number.isFinite(cor.g) && Number.isFinite(cor.b)
        ? { cor, motivo: 'lida' }
        : ILEGIVEL;
    }
    case 'hsl':
    case 'hsla':
      return p.length < 3
        ? ILEGIVEL
        : fechar(deHsl(angulo(p[0]), fracao(p[1]), fracao(p[2]), alfa(p[3])));
    case 'hwb':
      return p.length < 3
        ? ILEGIVEL
        : fechar(deHwb(angulo(p[0]), fracao(p[1]), fracao(p[2]), alfa(p[3])));
    case 'lab':
      return p.length < 3
        ? ILEGIVEL
        : fechar(deLab(num(p[0], 100), num(p[1], 125), num(p[2], 125), alfa(p[3])));
    case 'lch': {
      if (p.length < 3) return ILEGIVEL;
      const [aa, bb] = polar(num(p[1], 150), angulo(p[2]));
      return fechar(deLab(num(p[0], 100), aa, bb, alfa(p[3])));
    }
    case 'oklab':
      return p.length < 3
        ? ILEGIVEL
        : fechar(deOklab(num(p[0], 1), num(p[1], 0.4), num(p[2], 0.4), alfa(p[3])));
    case 'oklch': {
      if (p.length < 3) return ILEGIVEL;
      const [aa, bb] = polar(num(p[1], 0.4), angulo(p[2]));
      return fechar(deOklab(num(p[0], 1), aa, bb, alfa(p[3])));
    }
    case 'color': {
      /*
       * `color(<espaço> r g b)` — o formato que o Chromium devolve para quem
       * escreveu `color(srgb …)`, e o valor exato que quebrou a medição de uma
       * página preta inteira.
       *
       * Os espaços que faltam (`a98-rgb`, `prophoto-rgb`, `rec2020`) caem em
       * `ilegivel` de propósito: cada um é outra matriz, e o aviso passa a
       * dizer que **eu** não li — que é a informação certa até alguém aparecer
       * com um site que use algum deles.
       */
      if (p.length < 4) return ILEGIVEL;
      const espaco = p[0]!;
      const c: Vetor3 = [num(p[1], 1), num(p[2], 1), num(p[3], 1)];
      const a = alfa(p[4]);
      if (!c.every(Number.isFinite)) return ILEGIVEL;
      switch (espaco) {
        case 'srgb':
          return fechar(de01(c[0], c[1], c[2], a));
        case 'srgb-linear':
          return fechar(deSrgbLinear(c, a));
        case 'display-p3':
          return fechar(
            deXyzD65(matriz(P3_LINEAR_PARA_XYZ_D65, c.map(semGama) as Vetor3), a),
          );
        case 'xyz':
        case 'xyz-d65':
          return fechar(deXyzD65(c, a));
        case 'xyz-d50':
          return fechar(deXyzD65(matriz(XYZ_D50_PARA_D65, c), a));
        default:
          return ILEGIVEL;
      }
    }
    default:
      return ILEGIVEL;
  }
}

/** A cor, ou `null` quando ela não existe **ou** quando não sei ler — ver `lerCor`. */
export function parseCor(valor: string | null | undefined): Rgb | null {
  return lerCor(valor).cor;
}

export function paraHex(cor: Rgb): string {
  const h = (n: number) => limitar(Math.round(n), 0, 255).toString(16).padStart(2, '0');
  const base = `#${h(cor.r)}${h(cor.g)}${h(cor.b)}`;
  return cor.a >= 1 ? base : `${base}${h(cor.a * 255)}`;
}

/**
 * Achata uma cor com alfa contra um fundo.
 *
 * Sem isto, todo cálculo de contraste sobre texto semitransparente mente — e
 * texto semitransparente é a forma mais comum de "texto suave" na web
 * (`rgba(0,0,0,.6)`). Comparar esse valor direto com o fundo dá uma razão que
 * não existe na tela.
 */
export function achatar(frente: Rgb, fundo: Rgb): Rgb {
  if (frente.a >= 1) return frente;
  const m = (f: number, t: number) => f * frente.a + t * (1 - frente.a);
  return { r: m(frente.r, fundo.r), g: m(frente.g, fundo.g), b: m(frente.b, fundo.b), a: 1 };
}

/**
 * Luminância relativa, WCAG 2.x.
 *
 * ## O degrau é 0,04045, e por que ele mudou
 *
 * Estava 0,03928 aqui e 0,04045 em `area/contraste.ts`, e as duas se diziam
 * "WCAG 2.x". As duas estavam citando a norma certa: o texto da WCAG 2.0/2.1
 * escreve 0,03928, herdado de um rascunho antigo do sRGB, e a **WCAG 2.2**
 * corrigiu para 0,04045 — que é onde as duas metades da curva de fato se
 * encontram. Com 0,03928 a função tem uma descontinuidade minúscula; com
 * 0,04045, não tem.
 *
 * Ter dois números para a mesma constante em duas funções que se chamam
 * `contraste` era uma dúvida legítima do tipo "podem discordar na margem". Elas
 * **não podem**, e isso está medido em `contraste-de-acordo.test.ts`: nos 256
 * valores de canal de 8 bits a diferença é **exatamente zero** — nenhum inteiro
 * de 0 a 255 cai entre os dois degraus. A diferença só existe em canal
 * fracionário (o que sai de `achatar`), e lá o pior caso é 7,6e-7 de luminância
 * e 2,8e-4 de razão — quatro ordens de grandeza abaixo de qualquer limiar.
 *
 * Ou seja: a mudança não move número nenhum que alguém já tenha visto. Ela tira
 * a pergunta de circulação.
 *
 * A duplicação em si **fica**, e de propósito: `area/contraste.ts` é a régua do
 * certificado e do tema do aluno, escrita sem `import` nenhum para o bundle do
 * aluno não pagar por ela; esta aqui é a do estúdio de design, e é a única das
 * duas que sabe achatar alfa. Escopos diferentes, entradas diferentes. O que não
 * podia ficar diferente era a constante.
 */
export function luminancia(cor: Rgb): number {
  const c = (v: number) => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * c(cor.r) + 0.7152 * c(cor.g) + 0.0722 * c(cor.b);
}

/**
 * Razão de contraste entre 1 e 21.
 *
 * A frente é achatada contra o fundo antes da conta — ver `achatar`. A ordem
 * dos argumentos não importa para o resultado, mas importa para o achatamento:
 * passe (texto, fundo).
 */
export function contraste(frente: Rgb, fundo: Rgb): number {
  const f = luminancia(achatar(frente, fundo));
  const t = luminancia(fundo);
  const claro = Math.max(f, t);
  const escuro = Math.min(f, t);
  return (claro + 0.05) / (escuro + 0.05);
}

/** `AAA` ≥ 7, `AA` ≥ 4.5, `AA-grande` ≥ 3 (texto ≥ 24px ou ≥ 18.66px em negrito). */
export type NivelWcag = 'AAA' | 'AA' | 'AA-grande' | 'reprovado';

export function nivelWcag(razao: number, textoGrande = false): NivelWcag {
  if (razao >= 7) return 'AAA';
  if (razao >= 4.5) return 'AA';
  if (textoGrande && razao >= 3) return 'AA-grande';
  return 'reprovado';
}

export interface Hsl {
  /** 0 a 360. */
  h: number;
  /** 0 a 1. */
  s: number;
  /** 0 a 1. */
  l: number;
}

export function paraHsl(cor: Rgb): Hsl {
  const r = cor.r / 255;
  const g = cor.g / 255;
  const b = cor.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;

  if (d === 0) return { h: 0, s: 0, l };

  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;

  return { h: h * 360, s, l };
}

/**
 * Quão diferentes duas cores parecem, de 0 a ~1.
 *
 * ## Por que não é distância euclidiana em RGB
 *
 * Porque RGB não é perceptual: `#0000ff` e `#000080` estão longe em RGB e o
 * olho os lê como o mesmo azul em dois tons, enquanto `#808080` e `#7a8280`
 * estão perto e são visivelmente cinzas diferentes.
 *
 * A aproximação usada aqui é a "redmean" — uma correção barata que pondera os
 * canais pela luminosidade média do vermelho. Não é CIEDE2000, e não precisa
 * ser: o uso é agrupar tons vizinhos numa paleta de seis. Errar por pouco
 * junta dois cinzas quase iguais, que é o que a gente quer mesmo.
 */
export function distancia(a: Rgb, b: Rgb): number {
  const rm = (a.r + b.r) / 2;
  const dr = a.r - b.r;
  const dg = a.g - b.g;
  const db = a.b - b.b;
  const bruta = Math.sqrt(
    (2 + rm / 256) * dr * dr + 4 * dg * dg + (2 + (255 - rm) / 256) * db * db,
  );
  // O máximo teórico é ~765; normalizar deixa o limiar de agrupamento legível.
  return bruta / 765;
}

/**
 * `true` quando as duas cores são próximas o bastante para virarem uma só.
 *
 * ## De onde vem 0,02
 *
 * De medir os casos que importam, não de escolher um número redondo:
 *
 * | par | distância | deve agrupar? |
 * |---|---|---|
 * | `#1a1a1a` × `#191919` — o mesmo preto em dois componentes | 0,004 | sim |
 * | `#2d322f` × `#2e332f` — a mesma tinta com arredondamento | 0,003 | sim |
 * | `#ebedea` × `#e2e5e1` — **`surface` × `desk` do próprio Vestigo** | 0,034 | **não** |
 * | `#ffffff` × `#f2f1ee` — branco × papel | 0,057 | não |
 * | `#1f6f4a` × `#b4432c` — verde × terracota | 0,330 | não |
 *
 * A terceira linha é a que fixa o teto. Papel e mesa diferem por três pontos de
 * cinza e essa diferença **é** o sistema — um limiar que as junte apaga
 * justamente a distinção entre fundo de página e superfície de conteúdo, que é
 * um dos seis tokens que a paleta precisa devolver.
 */
export function saoVizinhas(a: Rgb, b: Rgb, limiar = 0.02): boolean {
  return distancia(a, b) <= limiar;
}

/**
 * ΔE CIE76 entre duas cores — a unidade em que os limiares da §J1.2 estão
 * escritos.
 *
 * ## Por que não serve `distancia`
 *
 * `distancia` é a redmean normalizada em 0..1, e ela existe para **agrupar**
 * tons vizinhos numa paleta de seis: errar por pouco junta dois cinzas quase
 * iguais, que é o desejado ali. O juiz escreveu *"ΔE ≤ 12"* e *"ΔE ≤ 25"* — e
 * esses números só querem dizer alguma coisa na escala de Lab, onde ~2,3 é o
 * limiar do olho e 100 é preto contra branco. Reaproveitar `distancia` aqui
 * seria reinterpretar em silêncio o número que a régua fixou antes da leva.
 *
 * ## Por que CIE76 e não CIEDE2000
 *
 * Porque CIEDE2000 tem trinta linhas de correção trigonométrica e o que este
 * eixo pergunta é *"o âmbar voltou lilás?"*, não *"estes dois beges são o
 * mesmo?"*. CIE76 erra na região azul-saturada, e nenhum limiar desta régua
 * depende dessa região. Se um dia depender, esta função troca de miolo e os
 * limiares continuam nos mesmos números — que é a razão de ela ser uma função.
 *
 * O alfa é achatado por quem chama: uma cor translúcida sem fundo não tem ΔE.
 */
export interface Lab {
  /** Claridade perceptual, 0 (preto) a 100 (branco). */
  L: number;
  /** Eixo verde–vermelho. */
  a: number;
  /** Eixo azul–amarelo. */
  b: number;
}

/**
 * CIELab D65 — e ela é exportada porque `deltaE` não é a única pergunta.
 *
 * Nasceu privada, a serviço do ΔE. Deixou de ser quando a rampa de densidade
 * (`--color-densidade`, ver o `@theme` de `apps/web/src/index.css`) precisou de
 * uma coisa que ΔE não responde: **a ordem**. «Estes dois degraus são
 * diferentes?» é ΔE; «este degrau é mais escuro que o anterior?» é L*, e
 * comparar dois ΔE nunca devolve um sinal.
 *
 * Reexpor a conta que já existe é o oposto de escrever a segunda: a régua de
 * contraste, a de ΔE e a de monotonia passam a discordar juntas ou concordar
 * juntas, que é a única forma de este repositório não ter duas respostas para a
 * mesma cor — o defeito que `contraste-de-acordo.test.ts` foi escrito para
 * fechar do outro lado.
 */
export function paraLab(c: Rgb): Lab {
  /* sRGB → linear. A curva de 2,4 com o joelho em 0,04045 é a do padrão; usar
     um gama 2,2 simples desloca o L* dos tons escuros o bastante para mover um
     ΔE de 12 para 14 — dentro da faixa em que este limiar decide. */
  const lin = (v: number): number => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const r = lin(c.r);
  const g = lin(c.g);
  const b = lin(c.b);

  /* sRGB D65 → XYZ, normalizado pelo branco D65. */
  const x = (0.4124564 * r + 0.3575761 * g + 0.1804375 * b) / 0.95047;
  const y = 0.2126729 * r + 0.7151522 * g + 0.072175 * b;
  const z = (0.0193339 * r + 0.119192 * g + 0.9503041 * b) / 1.08883;

  const f = (t: number): number => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  const fx = f(x);
  const fy = f(y);
  const fz = f(z);

  return { L: 116 * fy - 16, a: 500 * (fx - fy), b: 200 * (fy - fz) };
}

/**
 * CIELab **D65** → sRGB: o caminho de volta de `paraLab`, e só dele.
 *
 * ## O nome é feio de propósito, e o motivo é um alçapão
 *
 * Este arquivo já tem um `deLab` privado, na parte de LEITURA de cor, e ele
 * **não é o inverso desta função**: aquele é o `lab()` do CSS Color 4, que é
 * **D50**, e este é o par de `paraLab`, que é **D65**. Os dois recebem três
 * números chamados L, a e b e devolvem cores diferentes para os mesmos três
 * números. Chamar os dois de `deLab` faria a próxima pessoa usar o que estivesse
 * à mão, e o erro sairia como um desvio de matiz pequeno demais para acusar
 * alguém e grande o bastante para mover um ΔE de 20 para 18.
 *
 * ## Por que ela existe
 *
 * Nasceu com a rampa de calor do mapa do V-View (29/08/2026), e nasceu AQUI
 * e não lá pelo motivo que já trouxe `paraLab` para o exportado: **interpolar
 * duas tintas quentes em sRGB passa por um marrom sujo no meio**, e a única
 * forma de não passar é fazer a conta no espaço perceptual e voltar. Voltar
 * exige esta função; escrevê-la no `lib/paleta.ts` do painel daria a este
 * repositório duas respostas para «que cor é este Lab» — que é exatamente o
 * defeito que o docblock de `paraLab` diz ter fechado do outro lado.
 *
 * **Ela grampeia no cubo sRGB, e isso é parte do contrato, não um descuido.**
 * Lab descreve cores que o monitor não tem; devolver um canal negativo faria
 * `paraHex` escrever lixo. Quem precisa saber se a cor coube confere levando o
 * resultado de volta a `paraLab`: se o L* voltar diferente do que entrou, a
 * tinta estava fora do gamute. É assim que a busca que escolheu a ponta quente
 * da rampa (29/08/2026) descartou as candidatas que o sRGB não tem: pedia-se
 * L* 23 com croma 65, o cubo devolvia outra coisa, e a comparação de ida e volta
 * era o único filtro que via isso.
 */
export function corDeLab(lab: Lab): Rgb {
  const fy = (lab.L + 16) / 116;
  const fx = fy + lab.a / 500;
  const fz = fy - lab.b / 200;
  const inv = (t: number): number => (t ** 3 > 0.008856 ? t ** 3 : (t - 16 / 116) / 7.787);
  const x = inv(fx) * 0.95047;
  const y = inv(fy);
  const z = inv(fz) * 1.08883;

  /* A mesma matriz de `paraLab`, invertida, e a mesma curva de 2,4 — não uma
     gama 2,2 aproximada. Ida e volta com curvas diferentes desloca o L* dos
     tons escuros, que é a faixa em que a ponta quente desta rampa vive. */
  const enc = (v: number): number => {
    const s = v <= 0.0031308 ? v * 12.92 : 1.055 * v ** (1 / 2.4) - 0.055;
    return Math.max(0, Math.min(255, Math.round(s * 255)));
  };
  return {
    r: enc(3.2404542 * x - 1.5371385 * y - 0.4985314 * z),
    g: enc(-0.969266 * x + 1.8760108 * y + 0.041556 * z),
    b: enc(0.0556434 * x - 0.2040259 * y + 1.0572252 * z),
    a: 1,
  };
}

export function deltaE(a: Rgb, b: Rgb): number {
  const la = paraLab(a);
  const lb = paraLab(b);
  return Math.sqrt((la.L - lb.L) ** 2 + (la.a - lb.a) ** 2 + (la.b - lb.b) ** 2);
}

/**
 * O croma em CIELab — o quanto a cor está longe do cinza de mesma claridade.
 *
 * `paraHsl().s` responde outra coisa e responde errado para esta pergunta: a
 * saturação de HSL é uma razão dentro do cubo RGB, e por isso um amarelo puro e
 * um azul puro dão os dois 100% quando o olho lê o amarelo como muito mais
 * colorido. Croma é medido no espaço perceptual e é a unidade em que «a ponta
 * quente lê como quente» vira número — 6,5 no degrau mais fraco da rampa de
 * densidade, 49,4 no mais forte.
 *
 * O alfa é achatado por quem chama, pela mesma razão de `deltaE`: uma cor
 * translúcida sem fundo não tem croma.
 */
export function croma(c: Rgb): number {
  const l = paraLab(c);
  return Math.sqrt(l.a * l.a + l.b * l.b);
}

/**
 * A distância entre dois matizes, em graus, pelo caminho curto.
 *
 * Existe por causa da emenda do juiz ao `E4`: o acento tem de passar em 4,5:1,
 * que é item grave e barra publicação, e exigir o hexadecimal da referência
 * construiria um impasse que nenhuma leva resolve. **O que a fidelidade cobra do
 * acento é a família do matiz** — o âmbar não pode voltar lilás —, não a tinta
 * exata. Um matiz é circular, e 350° contra 10° são 20 graus e não 340.
 */
export function distanciaDeMatiz(a: Rgb, b: Rgb): number {
  const d = Math.abs(paraHsl(a).h - paraHsl(b).h) % 360;
  return d > 180 ? 360 - d : d;
}

/**
 * Se a cor pode ser ignorada por não pintar nada.
 *
 * Quase-transparente é o caso que derrubou o extrator original: `#0000` — a
 * sentinela de sombra vazia do Tailwind — foi a "cor mais frequente" de uma
 * captura real, com 310 ocorrências, por ser contada como cor. **É isto que
 * esta função protege, e continua protegendo.**
 *
 * ## Por que ela deixou de aceitar `null`
 *
 * Porque a assinatura antiga era `Rgb | null` e respondia `true` para `null` —
 * ou seja, *uma cor que eu não soube ler era indistinguível de transparente*.
 * O tipo era o único lugar onde essa confusão podia ser desfeita de uma vez:
 * exigindo `Rgb`, o compilador obriga cada chamador a decidir o que fazer com
 * a leitura que falhou, em vez de deixá-la cair no mesmo balde do `#0000`.
 *
 * Quem tem um valor cru na mão chama `lerCor` e olha o `motivo`.
 */
export function ehInvisivel(cor: Rgb): boolean {
  return cor.a < 0.06;
}
